import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, Search, Menu, X, CircleHelp } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const navPrimary = [
  { to: "/usa", label: "USA" },
  { to: "/pulse", label: "Pulse", dot: "green" },
  { to: "/money-flow", label: "Money Flow", dot: "orange" },
  { to: "/automations", label: "Automations", dot: "blue" }
];

const dropdowns = [
  { label: "Learn", items: [ { to: "/library", label: "Framework Library" }, { to: "/glossary", label: "Risk Glossary" }, { to: "/pulse", label: "Newsletters" }, { to: "/usa", label: "Site Guide" } ] },
  { label: "Watch", items: [ { to: "/pulse", label: "Live monthly webinars" }, { to: "/pulse", label: "RiskPedia Shorts" } ] },
  { label: "Tools", items: [ { to: "/tools", label: "All tools" }, { to: "/tools/grc-maturity", label: "GRC Maturity Scorer" }, { to: "/tools/compliance-cost", label: "Non-Compliance Cost" }, { to: "/tools/deadline-calendar", label: "Deadline Calendar" } ] }
];

function Dropdown({ label, items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 px-3 py-2 text-[14px] font-medium text-[#1A1914]/80 hover:text-[#1A1914] rp-btn rounded-lg hover:bg-[#F3EEE4]">
        {label} <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 w-60 bg-white border border-[#E7E0D2] rounded-xl shadow-xl py-2 z-50 rp-fade-up" style={{ animationDuration: "0.25s" }}>
          {items.map((it) => (
            <Link key={it.label} to={it.to} onClick={() => setOpen(false)} className="block px-4 py-2.5 text-[14px] text-[#1A1914]/80 hover:bg-[#FAF7F1] hover:text-[#3F4CE0]">{it.label}</Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dotColor = { green: "bg-[#12B76A]", orange: "bg-[#E8590C]", blue: "bg-[#3F4CE0]" };

  return (
    <header className={`sticky top-0 z-40 transition-shadow duration-300 ${scrolled ? "shadow-[0_2px_20px_-8px_rgba(26,25,20,0.25)]" : ""}`}>
      <div className="bg-[#FAF7F1]/95 backdrop-blur-md border-b border-[#E7E0D2]">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
                <span className="w-2.5 h-2.5 rounded-full bg-[#3F4CE0] group-hover:scale-125 transition-transform" />
                <span className="font-display font-bold text-[20px] tracking-tight">RiskPedia <span className="text-[#3F4CE0]">USA</span></span>
              </Link>
              <nav className="hidden lg:flex items-center gap-0.5">
                {navPrimary.map((n) => (
                  <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`} className={({ isActive }) => `flex items-center gap-1.5 px-3 py-2 text-[14px] font-medium rounded-lg rp-btn ${isActive ? "text-[#3F4CE0] bg-[#3F4CE0]/5" : "text-[#1A1914]/80 hover:text-[#1A1914] hover:bg-[#F3EEE4]"}`}>
                    {n.dot && <span className={`w-1.5 h-1.5 rounded-full ${dotColor[n.dot]}`} />}
                    {n.label}
                  </NavLink>
                ))}
                <Link to="/signup" className="flex items-center gap-1.5 px-3 py-1.5 ml-1 text-[13px] font-medium text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] rounded-full hover:bg-[#FDE68A] rp-btn">
                  Pro <span className="font-mono-label text-[10px]">$19/yr</span>
                </Link>
              </nav>
            </div>
            <div className="hidden lg:flex items-center gap-1">
              {dropdowns.map((d) => <Dropdown key={d.label} {...d} />)}
              <button aria-label="Help" className="p-2 rounded-lg hover:bg-[#F3EEE4] rp-btn text-[#1A1914]/60"><CircleHelp size={17} /></button>
              <button onClick={() => navigate("/glossary")} data-testid="nav-search" className="flex items-center gap-2 px-3 py-1.5 mx-1 text-[13px] text-[#1A1914]/50 border border-[#E7E0D2] rounded-lg bg-white hover:border-[#1A1914]/30 rp-btn">
                <Search size={14} /> Search <kbd className="px-1.5 py-0.5 text-[10px] bg-[#F3EEE4] rounded border border-[#E7E0D2]">/</kbd>
              </button>
              {user ? (
                <Link to="/profile" data-testid="nav-profile" className="flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border border-[#E7E0D2] bg-white hover:border-[#3F4CE0]/50 rp-btn">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" referrerPolicy="no-referrer" />
                  ) : (
                    <span className="w-7 h-7 rounded-full bg-[#3F4CE0] text-white text-[12px] font-bold flex items-center justify-center">{(user.name || user.email)[0]?.toUpperCase()}</span>
                  )}
                  <span className="text-[13.5px] font-semibold max-w-[110px] truncate">{user.name?.split(" ")[0] || "Account"}</span>
                </Link>
              ) : (
                <>
                  <Link to="/login" data-testid="nav-login" className="px-3 py-2 text-[14px] font-medium text-[#1A1914]/80 hover:text-[#1A1914] rounded-lg hover:bg-[#F3EEE4] rp-btn">Log in</Link>
                  <Link to="/signup" data-testid="nav-get-started" className="px-4 py-2 text-[14px] font-semibold text-white bg-[#3F4CE0] hover:bg-[#3341C6] rounded-lg rp-btn shadow-sm">Get started</Link>
                </>
              )}
            </div>
            <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>
      {mobileOpen && (
        <div className="lg:hidden bg-[#FAF7F1] border-b border-[#E7E0D2] px-5 py-4 space-y-1 rp-fade-up" style={{ animationDuration: "0.25s" }}>
          {[...navPrimary, { to: "/library", label: "Library" }, { to: "/glossary", label: "Glossary" }, { to: "/tools", label: "Tools" }, { to: "/admin", label: "Admin" }].map((n) => (
            <NavLink key={n.label} to={n.to} onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 text-[15px] font-medium rounded-lg hover:bg-[#F3EEE4]">{n.label}</NavLink>
          ))}
          <div className="pt-2 flex gap-2">
            <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1 text-center px-4 py-2.5 text-[14px] font-medium border border-[#E7E0D2] rounded-lg">Log in</Link>
            <Link to="/signup" onClick={() => setMobileOpen(false)} className="flex-1 text-center px-4 py-2.5 text-[14px] font-semibold text-white bg-[#3F4CE0] rounded-lg">Get started</Link>
          </div>
        </div>
      )}
    </header>
  );
}
